import { useNavigate } from 'react-router-dom';
import { Button } from '../atoms/Button/Button';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import type { HealthScoreResult } from '../../utils/healthScore';
import { HealthRing } from './HealthRing';
import './DashboardHero.css';

export interface DashboardHeroProps {
  /** Selected aquarium; used for the "add a water test / ICP" links. */
  aquariumId?: string;
  aquariumName?: string;
  aquariumSubtitle?: string;
  /**
   * Health score computed from the latest water values. `undefined` while the
   * values are loading. A score is never shown without data.
   */
  health?: HealthScoreResult;
  /** The water values could not be loaded — no score can be computed. */
  healthUnavailable?: boolean;
  lastIcp?: string;
  actionStatus?: string;
  /** Current time; injectable for tests. */
  now?: Date;
}

/** Translation key of the greeting for the local hour. */
export function greetingKey(hour: number): string {
  if (hour >= 5 && hour < 12) return 'dashboard.hero.greetingMorning';
  if (hour >= 12 && hour < 18) return 'dashboard.hero.greetingAfternoon';
  return 'dashboard.hero.greetingEvening';
}

export function DashboardHero({
  aquariumId,
  aquariumName,
  aquariumSubtitle,
  health,
  healthUnavailable = false,
  lastIcp,
  actionStatus,
  now,
}: DashboardHeroProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const greeting = t(greetingKey((now ?? new Date()).getHours()));

  const score = !healthUnavailable && health ? health.score : null;
  const level = score === null ? 'none' : health?.level ?? 'none';
  let statusLabel: string;
  if (healthUnavailable) statusLabel = t('dashboard.hero.healthUnavailable');
  else if (health === undefined) statusLabel = t('dashboard.hero.healthLoading');
  else if (score === null) statusLabel = t('dashboard.hero.noData');
  else statusLabel = t(`dashboard.hero.level.${level}`);

  const scoreText = score === null ? '—' : new Intl.NumberFormat(i18n.language).format(score);
  const showNoDataHint = !healthUnavailable && health !== undefined && score === null;

  return (
    <section className="rg-dashboard-hero" aria-label={t('dashboard.hero.regionLabel')}>
      <div className="rg-dashboard-hero__content">
        <div>
          <Text className="rg-dashboard-hero__eyebrow">{greeting}</Text>
          <Heading level={2} className="rg-dashboard-hero__title">
            {aquariumName ?? t('dashboard.hero.defaultName')}
          </Heading>
          {aquariumSubtitle && (
            <Text className="rg-dashboard-hero__subtitle">{aquariumSubtitle}</Text>
          )}
        </div>
        {(lastIcp != null || actionStatus != null) && (
          <div className="rg-dashboard-hero__meta">
            {lastIcp && <span>{lastIcp}</span>}
            {actionStatus && <span>{actionStatus}</span>}
          </div>
        )}
      </div>
      <div className="rg-dashboard-hero__score" data-testid="dashboard-health">
        <HealthRing value={scoreText} label={t('dashboard.hero.healthScore')} statusLabel={statusLabel} hideCaption />
        <div className="rg-dashboard-hero__score-text">
          {/* Already part of the ring's accessible name. */}
          <Text as="span" variant="caption" className="rg-dashboard-hero__score-caption" aria-hidden="true">
            {t('dashboard.hero.healthScore')}
          </Text>
          <span className={`rg-dashboard-hero__status rg-dashboard-hero__status--${level}`}>{statusLabel}</span>
          {health?.partial === true && score !== null && (
            <Text size="sm" className="rg-dashboard-hero__score-note">
              {t('dashboard.hero.partialData', { count: health.scoredCount, total: health.totalCount })}
            </Text>
          )}
          {showNoDataHint && (
            <>
              <Text size="sm" className="rg-dashboard-hero__score-note">
                {t('dashboard.hero.noDataHint')}
              </Text>
              {aquariumId && (
                <div className="rg-dashboard-hero__hint-actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/aquariums/${aquariumId}/measurements/new`)}
                  >
                    {t('dashboard.hero.addWaterTest')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/aquariums/${aquariumId}/icp-analyses`)}
                  >
                    {t('dashboard.hero.importIcp')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
